import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { useBannerOffset } from "@hooks/useBannerOffset";

import instance from "@store/middleware/api";

import { bannerSurface } from "@utils/bannerGradient";

import { useAuth } from "@/hooks/auth";
import { openUpdatePaymentModal } from "@/layouts/components/GlobalUpdatePaymentModal";

/**
 * Site-wide non-dismissible banner shown on every page when the
 * logged-in user has a subscription in PAST_DUE state.
 *
 * Hidden on /account and /account/subscription — both pages have
 * richer in-page banners (with the downgrade-to-Free warning copy on
 * /account/subscription, and the 1px red border on the payment-method
 * card on /account) so showing this slimmer global banner there too
 * would be visual noise.
 *
 * Mounted inside `SecondHeaderContainer` so it sits below the nav row
 * within the same fixed container — same pattern as
 * `PendingEmailGlobalBanner`.
 *
 * CTA opens the update-payment modal in-place via
 * `openUpdatePaymentModal({ isPastDue: true })` (GlobalUpdatePaymentModal in
 * the layout) — no navigation, no second click. A successful card update runs
 * setDefaultPaymentMethod, which retries the open invoice and recovers the sub.
 *
 * Fetches `/subscriptions/my` on auth + route change so fixing the
 * payment method on /account flips the banner off on the next page nav.
 */
const PastDueGlobalBanner: React.FC = () => {
  const { pathname } = useLocation();
  const { myData } = useAuth();
  const userId = (myData as any)?.id ?? (myData as any)?._id ?? null;
  const [isPastDue, setIsPastDue] = useState(false);
  const bannerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!userId) {
      setIsPastDue(false);
      return;
    }
    instance.get("/subscriptions/my")
      .then(({ data }) => setIsPastDue(data?.status === "PAST_DUE"))
      .catch(() => setIsPastDue(false));
  }, [userId, pathname]);

  // Publish the banner's actual rendered height to a CSS variable so
  // page layouts can offset their top content by the extra strip. Without
  // this, the banner just inflates the fixed SecondHeader and hides
  // every page's title row underneath.
  useBannerOffset("--past-due-banner-h", isPastDue, bannerRef);

  if (!isPastDue) return null;
  if (pathname === "/account" || pathname === "/account/") return null;
  if (pathname === "/account/subscription" || pathname === "/account/subscription/") return null;

  const goToPaymentMethod = () => {
    // Open the update-payment modal in-place on the current page (no nav,
    // no second click). Handled by GlobalUpdatePaymentModal in the layout.
    openUpdatePaymentModal({ isPastDue: true });
  };

  return (
    <div
      data-banner-surface
      ref={bannerRef}
      data-global-banner
      style={{
        width: "100%",
        marginTop: -2,
        ...bannerSurface("rgb(195, 75, 54)"),
        padding: "12px 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 16,
        color: "#fff",
        fontFamily: '"Raleway", sans-serif',
        fontSize: "0.875rem",
      }}
    >
      <span style={{ fontWeight: 500 }}>
        Your last payment failed and services are stopped. Update your payment method to continue your subscription.
      </span>
      <button
        onClick={goToPaymentMethod}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          padding: "6px 14px",
          borderRadius: 6,
          background: "linear-gradient(135deg, #ffd29b 0%, #ffebb5 100%)",
          border: "none",
          color: "#2a1d14",
          fontFamily: '"Raleway", sans-serif',
          fontSize: "0.8125rem",
          fontWeight: 600,
          cursor: "pointer",
          whiteSpace: "nowrap",
          boxShadow: "0 0 12px rgba(186, 144, 92, 0.25)",
          transition: "all 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 16px rgba(186, 144, 92, 0.5)";
          e.currentTarget.style.transform = "translateY(-1px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "0 0 12px rgba(186, 144, 92, 0.25)";
          e.currentTarget.style.transform = "translateY(0)";
        }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#60a3d7" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <rect x="1" y="4" width="22" height="16" rx="2" />
          <line x1="1" y1="10" x2="23" y2="10" />
        </svg>
        Update Payment Method
      </button>
    </div>
  );
};

export default PastDueGlobalBanner;
